import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

export interface EncryptedPayload {
  data: string;
  iv?: string;
  tag?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CryptoService {
  private cbcKeyPromise: Promise<CryptoKey> | null = null;
  private gcmKeyPromise: Promise<CryptoKey> | null = null;

  /**
   * Helper to convert Hex string (e.g. 64 chars key / 32 chars IV) to Uint8Array bytes
   */
  private hexToUint8Array(hexString: string): Uint8Array {
    const cleanHex = hexString.trim().replace(/^0x/i, '');
    if (cleanHex.length % 2 !== 0) {
      throw new Error('Invalid hex string length');
    }
    const bytes = new Uint8Array(cleanHex.length / 2);
    for (let i = 0; i < cleanHex.length; i += 2) {
      bytes[i / 2] = parseInt(cleanHex.substring(i, i + 2), 16);
    }
    return bytes;
  }

  /**
   * Safe URL decoder for percent-encoded Base64 strings (e.g. %2F, %2B, %3D)
   */
  private safeUrlDecode(str: string): string {
    if (typeof str !== 'string') return str;
    try {
      return decodeURIComponent(str);
    } catch {
      return str
        .replace(/%2F/gi, '/')
        .replace(/%2B/gi, '+')
        .replace(/%3D/gi, '=')
        .replace(/%20/g, '+');
    }
  }

  /**
   * Import AES-CBC CryptoKey from Hex ENCRYPTION_KEY
   */
  private async getCbcKey(): Promise<CryptoKey> {
    if (!this.cbcKeyPromise) {
      this.cbcKeyPromise = (async () => {
        const keyHex = environment.encryptionKey || 'e9aae337b7db6dac889e07a9425c3b52d2bb6ef7ff9313cf686d022e50aeeb89';
        const keyBytes = this.hexToUint8Array(keyHex);
        return window.crypto.subtle.importKey(
          'raw',
          keyBytes.buffer as ArrayBuffer,
          { name: 'AES-CBC' },
          false,
          ['encrypt', 'decrypt']
        );
      })();
    }
    return this.cbcKeyPromise;
  }

  /**
   * Import AES-GCM CryptoKey from Hex ENCRYPTION_KEY
   */
  private async getGcmKey(): Promise<CryptoKey> {
    if (!this.gcmKeyPromise) {
      this.gcmKeyPromise = (async () => {
        const keyHex = environment.encryptionKey || 'e9aae337b7db6dac889e07a9425c3b52d2bb6ef7ff9313cf686d022e50aeeb89';
        const keyBytes = this.hexToUint8Array(keyHex);
        return window.crypto.subtle.importKey(
          'raw',
          keyBytes.buffer as ArrayBuffer,
          { name: 'AES-GCM' },
          false,
          ['encrypt', 'decrypt']
        );
      })();
    }
    return this.gcmKeyPromise;
  }

  /**
   * Get IV bytes from environment configuration
   */
  private getIvBytes(overrideIvHex?: string): Uint8Array {
    const ivHex = overrideIvHex || environment.encryptionIv || '3d479594c3df2e78fde9f92f82d1b98a';
    return this.hexToUint8Array(ivHex);
  }

  /**
   * Encrypts plain object or string to a plain Base64 encrypted string (AES-256-CBC).
   * Direct plain string output without object wrappers.
   */
  async encryptString(data: any): Promise<string> {
    try {
      const key = await this.getCbcKey();
      const ivBytes = this.getIvBytes();
      const stringData = typeof data === 'string' ? data : JSON.stringify(data);
      const encodedData = new TextEncoder().encode(stringData);

      const encryptedBuffer = await window.crypto.subtle.encrypt(
        { name: 'AES-CBC', iv: ivBytes as BufferSource },
        key,
        encodedData
      );

      return this.arrayBufferToBase64(encryptedBuffer);
    } catch (error) {
      console.error('Encryption string failed:', error);
      throw error;
    }
  }

  /**
   * Encrypts plain object or string to AES-256-CBC or AES-256-GCM payload object.
   */
  async encrypt(data: any, algorithm: 'AES-CBC' | 'AES-GCM' = 'AES-CBC'): Promise<EncryptedPayload> {
    const cipherTextBase64 = await this.encryptString(data);
    return {
      iv: environment.encryptionIv,
      data: cipherTextBase64
    };
  }

  /**
   * Decrypts encrypted payload (plain Base64 string or object) using AES-256-CBC or AES-256-GCM.
   * If decryption fails (e.g. unencrypted backend response), gracefully falls back to raw data/JSON.
   */
  async decrypt<T = any>(payload: EncryptedPayload | string): Promise<T> {
    try {
      let cipherTextBase64: string;
      let ivHex: string | undefined;
      let tagBase64: string | undefined;

      if (typeof payload === 'string') {
        cipherTextBase64 = payload;
      } else if (typeof payload === 'object' && payload !== null) {
        cipherTextBase64 = payload.data || (payload as any).encryptedData || payload;
        ivHex = payload.iv;
        tagBase64 = payload.tag;
      } else {
        cipherTextBase64 = String(payload);
      }

      // Safe URL-decode if payload contains percent-encoded characters (%2F, %2B, %3D)
      cipherTextBase64 = this.safeUrlDecode(cipherTextBase64);

      // Clean up string quotes if payload was double JSON encoded
      if (cipherTextBase64.startsWith('"') && cipherTextBase64.endsWith('"')) {
        cipherTextBase64 = cipherTextBase64.substring(1, cipherTextBase64.length - 1);
      }

      const ivBytes = this.getIvBytes(ivHex);
      let decryptedBuffer: ArrayBuffer;

      if (tagBase64) {
        // AES-GCM decryption
        const key = await this.getGcmKey();
        const cipherTextBytes = this.base64ToUint8Array(cipherTextBase64);
        const tagBytes = this.base64ToUint8Array(tagBase64);

        const combinedBuffer = new ArrayBuffer(cipherTextBytes.byteLength + tagBytes.byteLength);
        const combinedView = new Uint8Array(combinedBuffer);
        combinedView.set(cipherTextBytes, 0);
        combinedView.set(tagBytes, cipherTextBytes.byteLength);

        decryptedBuffer = await window.crypto.subtle.decrypt(
          { name: 'AES-GCM', iv: ivBytes as BufferSource, tagLength: 128 },
          key,
          combinedBuffer
        );
      } else {
        // AES-CBC decryption
        const key = await this.getCbcKey();
        const cipherTextBytes = this.base64ToUint8Array(cipherTextBase64);
        decryptedBuffer = await window.crypto.subtle.decrypt(
          { name: 'AES-CBC', iv: ivBytes as BufferSource },
          key,
          cipherTextBytes.buffer as ArrayBuffer
        );
      }

      const decryptedText = new TextDecoder().decode(decryptedBuffer);

      try {
        return JSON.parse(decryptedText) as T;
      } catch {
        return decryptedText as unknown as T;
      }
    } catch (error) {
      console.warn('Decryption failed, falling back to original payload:', error);
      // Fallback for when backend encryption is not yet deployed or returns plain JSON/string
      if (typeof payload === 'string') {
        try {
          return JSON.parse(payload) as T;
        } catch {
          return payload as unknown as T;
        }
      }
      return payload as T;
    }
  }

  private arrayBufferToBase64(buffer: Uint8Array | ArrayBuffer): string {
    const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private base64ToUint8Array(base64: string): Uint8Array {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }
}
